import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE, flagUrl } from "@/lib/api";
import { invalidateBackgroundsCache } from "@/lib/sectionBackground";
import { toBilingual } from "@/lib/bilingual";
import BilingualEditor from "@/components/BilingualEditor";
import { toast } from "sonner";
import { Download, Trash2, Edit3, Save, X, Send, Lock, Unlock, KeyRound, Plus, Copy, Settings, Layers, RotateCcw, Image as ImageIcon, Upload, ChevronDown, ChevronUp, Menu as MenuIcon } from "lucide-react";

const TABS = [
  { id: "publish", label: "Publicación" },
  { id: "home-page", label: "Página de inicio" },
  { id: "pools", label: "Pollas / Espacios" },
  { id: "special-pools", label: "Pollas Especiales" },
  { id: "invites", label: "Códigos de invitación" },
  { id: "teams", label: "Equipos y grupos" },
  { id: "ko-setup", label: "Definir Eliminatorias" },
  { id: "results", label: "Resultados de partidos" },
  { id: "tournament", label: "Resultado de torneo" },
  { id: "users", label: "Usuarios" },
  { id: "settings", label: "Redes sociales" },
  { id: "backgrounds", label: "Fondos de secciones" },
];

function AdminSectionPicker({ tabs, value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = tabs.find((t) => t.id === value) || tabs[0];
  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="relative" data-testid="admin-section-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-section-toggle"
        className="w-full flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-[var(--c-lime)] transition-colors px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <MenuIcon size={18} className="text-[var(--c-lime)] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">Sección</div>
            <div className="text-base md:text-lg font-display uppercase truncate">{current.label}</div>
          </div>
        </div>
        {open ? <ChevronUp size={18} className="shrink-0 text-zinc-300" /> : <ChevronDown size={18} className="shrink-0 text-zinc-300" />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <ul
            data-testid="admin-section-menu"
            className="absolute z-40 left-0 right-0 mt-1 bg-[#0A0A0A] border border-zinc-800 shadow-2xl max-h-[60vh] overflow-y-auto"
            role="listbox"
          >
            {tabs.map((t) => {
              const active = t.id === value;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`admin-section-option-${t.id}`}
                    onClick={() => { onChange(t.id); setOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm uppercase tracking-wider transition-colors flex items-center justify-between gap-3 border-b border-zinc-900 last:border-b-0 ${
                      active
                        ? "bg-[var(--c-lime)] text-black font-bold"
                        : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                    }`}
                  >
                    <span className="truncate">{t.label}</span>
                    {active && <span className="text-[10px] font-mono-data">●</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}



export default function Admin() {
  const [tab, setTab] = useState("publish");
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [appState, setAppState] = useState({ predictions_published: false });

  const teamByCode = useMemo(() => Object.fromEntries(teams.map((t) => [t.code, t])), [teams]);

  const load = async () => {
    const [m, t, u, st] = await Promise.all([
      api.get("/tournament/matches"),
      api.get("/tournament/teams"),
      api.get("/admin/users"),
      api.get("/app-state"),
    ]);
    setMatches(m.data);
    setTeams(t.data);
    setUsers(u.data);
    setAppState(st.data);
  };

  useEffect(() => { load(); }, []);

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem("polla_token");
      // Append a timestamp so the browser/CDN cache can never serve a stale
      // Excel. Each click is a unique URL.
      const r = await fetch(`${API_BASE}/admin/export-predictions.xlsx?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}`, "Cache-Control": "no-cache" },
        credentials: "include",
        cache: "no-store",
      });
      if (!r.ok) throw new Error("error " + r.status);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `polla-mundialista-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("No se pudo exportar: " + e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">PANEL ADMIN</div>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">Administración</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            Modifica resultados, ajusta pronósticos y exporta toda la base de datos en Excel para transparencia.
          </p>
        </div>
        <button onClick={exportXlsx} data-testid="export-xlsx-button"
          className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-3 text-xs flex items-center gap-2">
          <Download size={14} /> Descargar Excel
        </button>
      </div>

      <AdminSectionPicker tabs={TABS} value={tab} onChange={setTab} />

      {tab === "publish" && <PublishPanel appState={appState} onChange={load} />}
      {tab === "home-page" && <HomePagePanel />}
      {tab === "pools" && <PoolsPanel />}
      {tab === "special-pools" && <SpecialPoolsPanel />}
      {tab === "invites" && <InviteCodes />}
      {tab === "teams" && <TeamsPanel teams={teams} matches={matches} onChange={load} />}
      {tab === "ko-setup" && <KOSetup matches={matches} teams={teams} teamByCode={teamByCode} onChange={load} />}
      {tab === "results" && <MatchResults matches={matches} teamByCode={teamByCode} onChange={load} />}
      {tab === "tournament" && <TournamentResult teams={teams} />}
      {tab === "users" && <UsersList users={users} onChange={load} />}
      {tab === "settings" && <SettingsPanel />}
      {tab === "backgrounds" && <BackgroundsPanel />}
    </div>
  );
}

function MatchResults({ matches, teamByCode, onChange }) {
  const [filter, setFilter] = useState("all");
  const filtered = matches.filter((m) => filter === "all" || m.stage === filter);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">Filtro:</span>
        {["all", "group", "r32", "r16", "qf", "sf", "third", "final"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${
              filter === s ? "bg-[var(--c-lime)] text-black border-[var(--c-lime)]" : "border-zinc-800 text-zinc-400"
            }`}>{s === "all" ? "Todos" : s}</button>
        ))}
      </div>
      <div className="bg-zinc-900 border border-zinc-800 max-h-[60vh] overflow-y-auto">
        {filtered.map((m) => (
          <MatchEditRow key={m.match_id} m={m} teamByCode={teamByCode} onSaved={onChange} />
        ))}
      </div>
    </div>
  );
}

function MatchEditRow({ m, teamByCode, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [home, setHome] = useState(m.home);
  const [away, setAway] = useState(m.away);
  const [date, setDate] = useState(m.kickoff?.slice(0, 16) || "");
  const [venue, setVenue] = useState(m.venue || "");
  const [rh, setRh] = useState(m.result_home ?? "");
  const [ra, setRa] = useState(m.result_away ?? "");
  const [winner, setWinner] = useState(m.winner_team || "");

  // Keep local state in sync whenever the parent reloads the match data
  useEffect(() => {
    setHome(m.home);
    setAway(m.away);
    setDate(m.kickoff?.slice(0, 16) || "");
    setVenue(m.venue || "");
    setRh(m.result_home ?? "");
    setRa(m.result_away ?? "");
    setWinner(m.winner_team || "");
  }, [m]);

  const hasResult = m.result_home != null && m.result_away != null;

  const save = async () => {
    const payload = {};
    if (m.stage !== "group") {
      // KO match: home/away may be slot refs ("1A"). Only update if user changed to a known code
      if (home && home !== m.home) payload.home = home;
      if (away && away !== m.away) payload.away = away;
    } else {
      if (home && home !== m.home) payload.home = home;
      if (away && away !== m.away) payload.away = away;
    }
    if (date && new Date(date).toISOString() !== m.kickoff) payload.kickoff = new Date(date).toISOString();
    if (venue && venue !== m.venue) payload.venue = venue;
    if (rh !== "") payload.result_home = parseInt(rh, 10);
    if (ra !== "") payload.result_away = parseInt(ra, 10);
    if (winner) payload.winner_team = winner;
    try {
      await api.put(`/admin/match/${m.match_id}`, payload);
      toast.success(`${m.match_id} guardado`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const clearResult = async () => {
    if (!confirm(`¿Limpiar el resultado de ${m.match_id}? Quedará en blanco.`)) return;
    try {
      await api.delete(`/admin/match/${m.match_id}/result`);
      toast.success(`${m.match_id} reiniciado`);
      setRh("");
      setRa("");
      setWinner("");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error("No se pudo limpiar el resultado");
    }
  };

  const isKo = m.stage !== "group";
  return (
    <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3 text-sm flex-wrap" data-testid={`admin-match-${m.match_id}`}>
      <div className="font-mono-data text-[10px] text-zinc-500 w-16">{m.match_id}</div>
      <div className="font-mono-data text-[10px] text-zinc-400 w-24 truncate">{m.round_label}</div>
      <div className="font-mono-data text-[10px] text-zinc-400 w-20">{m.kickoff?.slice(0, 10)}</div>
      <div className="flex items-center gap-2 flex-1 min-w-[140px] justify-end">
        {!isKo && teamByCode[m.home] && <img src={flagUrl(teamByCode[m.home].flag)} className="w-5 h-3.5 object-cover" alt={m.home} />}
        <span className="font-mono-data text-xs">{m.home}</span>
      </div>
      {editing ? (
        <>
          <input value={rh} onChange={(e) => setRh(e.target.value)} type="number" min={0} className="score-input" data-testid={`admin-rh-${m.match_id}`} />
          <span>-</span>
          <input value={ra} onChange={(e) => setRa(e.target.value)} type="number" min={0} className="score-input" data-testid={`admin-ra-${m.match_id}`} />
        </>
      ) : (
        <div className="font-mono-data w-20 text-center font-bold">
          {m.result_home != null ? `${m.result_home} - ${m.result_away}` : <span className="text-zinc-600">—</span>}
        </div>
      )}
      <div className="flex items-center gap-2 flex-1 min-w-[140px]">
        <span className="font-mono-data text-xs">{m.away}</span>
        {!isKo && teamByCode[m.away] && <img src={flagUrl(teamByCode[m.away].flag)} className="w-5 h-3.5 object-cover" alt={m.away} />}
      </div>
      {editing && isKo && (
        <select value={winner} onChange={(e) => setWinner(e.target.value)} data-testid={`admin-winner-${m.match_id}`}
          className="bg-black border border-zinc-800 px-2 py-1 text-xs">
          <option value="">Ganador…</option>
          {Object.values(teamByCode).map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}
        </select>
      )}
      {editing ? (
        <div className="flex gap-1">
          <button onClick={save} data-testid={`admin-save-${m.match_id}`} className="p-2 bg-[var(--c-lime)] text-black" title="Guardar"><Save size={12} /></button>
          {hasResult && (
            <button onClick={clearResult} data-testid={`admin-clear-${m.match_id}`} className="p-2 border border-[var(--c-red)] text-[var(--c-red)] hover:bg-[var(--c-red)] hover:text-black" title="Limpiar resultado">
              <RotateCcw size={12} />
            </button>
          )}
          <button onClick={() => setEditing(false)} className="p-2 border border-zinc-800" title="Cancelar"><X size={12} /></button>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} data-testid={`admin-edit-${m.match_id}`} className="p-2 border border-zinc-800 hover:border-white" title="Editar"><Edit3 size={12} /></button>
      )}
    </div>
  );
}

function TournamentResult({ teams }) {
  const [value, setValue] = useState({ champion: "", runner_up: "", third: "" });
  useEffect(() => {
    api.get("/admin/tournament-result").then((r) => {
      setValue({
        champion: r.data.champion || "",
        runner_up: r.data.runner_up || "",
        third: r.data.third || "",
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/tournament-result", value);
      toast.success("Resultado de torneo guardado");
    } catch {
      toast.error("Error al guardar");
    }
  };

  const clearAll = async () => {
    if (!confirm("¿Limpiar el resultado de torneo (campeón, subcampeón y tercer puesto)?")) return;
    try {
      await api.delete("/admin/tournament-result");
      setValue({ champion: "", runner_up: "", third: "" });
      toast.success("Resultado de torneo limpiado");
    } catch {
      toast.error("Error al limpiar");
    }
  };

  const sorted = [...teams].sort((a, b) => a.name.localeCompare(b.name));
  const slot = (label, key, color) => (
    <div className="bg-zinc-900 border border-zinc-800 p-6 flex-1">
      <div className="text-[10px] uppercase tracking-widest font-mono-data mb-2" style={{ color }}>{label}</div>
      <select value={value[key] || ""} onChange={(e) => setValue({ ...value, [key]: e.target.value || null })}
        data-testid={`admin-tournament-${key}`}
        className="w-full bg-black border border-zinc-800 px-3 py-3 outline-none text-sm">
        <option value="">— Selecciona —</option>
        {sorted.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
      </select>
    </div>
  );
  const hasAny = value.champion || value.runner_up || value.third;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {slot("Campeón", "champion", "var(--c-yellow)")}
        {slot("Subcampeón", "runner_up", "var(--c-purple)")}
        {slot("Tercer puesto", "third", "var(--c-peach)")}
      </div>
      <div className="flex gap-3 flex-wrap">
        <button onClick={save} data-testid="admin-save-tournament"
          className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-6 py-3 text-xs">
          Guardar resultado de torneo
        </button>
        {hasAny && (
          <button onClick={clearAll} data-testid="admin-clear-tournament"
            className="border border-[var(--c-red)] text-[var(--c-red)] font-bold uppercase tracking-wider px-6 py-3 text-xs flex items-center gap-2 hover:bg-[var(--c-red)] hover:text-black">
            <RotateCcw size={12} /> Limpiar resultado
          </button>
        )}
      </div>
    </div>
  );
}


function UsersList({ users, onChange }) {
  const [resetTarget, setResetTarget] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const isLocked = (u) => {
    const lu = u.locked_until;
    if (!lu) return false;
    return new Date(lu).getTime() > Date.now();
  };
  const unlock = async (u) => {
    if (!confirm(`¿Desbloquear cuenta de ${u.nickname}?\n\nVa a poder volver a intentar iniciar sesión inmediatamente.`)) return;
    try {
      await api.post(`/admin/user/${u.user_id}/unlock`);
      toast.success(`${u.nickname} desbloqueado`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const remove = async (id) => {
    if (!confirm("¿Eliminar usuario y todos sus pronósticos?")) return;
    try {
      await api.delete(`/admin/user/${id}`);
      toast.success("Usuario eliminado");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const forceLogoutAll = async () => {
    const msg = "¿Cerrar la sesión de TODOS los usuarios?\n\nEsto invalida cada token JWT y sesión OAuth activa. Todos (incluyéndote a ti) deberán volver a iniciar sesión en su próxima acción.\n\nÚtil después de actualizar la app o si sospechas que algún token fue comprometido.";
    if (!confirm(msg)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/force-logout-all");
      toast.success(`Sesiones cerradas. ${r.data.oauth_sessions_revoked || 0} sesiones OAuth eliminadas.`);
      // The admin's own next request will return 401 and the global interceptor
      // will bounce them to /login automatically — nothing else to do here.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBulkBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="bg-zinc-900 border border-red-900/50 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-mono-data text-[var(--c-red)] mb-1">Zona crítica</div>
          <div className="text-sm">Cerrar todas las sesiones activas</div>
          <div className="text-xs text-zinc-500 mt-1">Invalida JWTs + sesiones OAuth. Te incluye a ti.</div>
        </div>
        <button
          onClick={forceLogoutAll}
          disabled={bulkBusy}
          data-testid="admin-force-logout-all"
          className="bg-[var(--c-red)] text-white font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50"
        >
          {bulkBusy ? "..." : "Cerrar sesión de todos"}
        </button>
      </div>
      <div className="bg-zinc-900 border border-zinc-800">
        {users.map((u) => (
          <div key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 text-sm flex-wrap">
            <div>
              <div className="font-bold flex items-center gap-2">
                {u.nickname}
                {isLocked(u) && (
                  <span
                    data-testid={`admin-user-locked-${u.user_id}`}
                    title={`Bloqueado hasta ${new Date(u.locked_until).toLocaleString()}`}
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono-data px-1.5 py-0.5 border border-[var(--c-red)] text-[var(--c-red)]"
                  >
                    <Lock size={9} /> Bloqueado
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500">{u.email}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-mono-data px-2 py-0.5 border border-zinc-800">
                {u.is_admin ? "ADMIN" : u.auth_type}
              </span>
              {isLocked(u) && (
                <button
                  onClick={() => unlock(u)}
                  data-testid={`admin-unlock-${u.user_id}`}
                  title="Desbloquear cuenta"
                  className="p-2 border border-zinc-800 text-[var(--c-lime)] hover:border-[var(--c-lime)]"
                >
                  <Unlock size={12} />
                </button>
              )}
              {!u.is_admin && (
                <button
                  onClick={() => setResetTarget(u)}
                  data-testid={`admin-reset-${u.user_id}`}
                  title="Restablecer contraseña"
                  className="p-2 border border-zinc-800 text-[var(--c-yellow)] hover:border-[var(--c-yellow)]"
                >
                  <KeyRound size={12} />
                </button>
              )}
              {!u.is_admin && (
                <button onClick={() => remove(u.user_id)} data-testid={`admin-delete-${u.user_id}`}
                  className="p-2 border border-zinc-800 text-[var(--c-red)] hover:border-[var(--c-red)]">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
      ))}
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [mode, setMode] = useState("auto"); // "auto" | "manual"
  const [manualPw, setManualPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { new_password }
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (mode === "manual" && manualPw.trim().length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    setBusy(true);
    try {
      const body = mode === "manual" ? { new_password: manualPw.trim() } : {};
      const r = await api.post(`/admin/user/${user.user_id}/reset-password`, body);
      setResult(r.data);
      toast.success("Contraseña restablecida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al restablecer");
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.new_password) return;
    try {
      await navigator.clipboard.writeText(result.new_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="reset-password-modal">
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl uppercase">Restablecer contraseña</h3>
          <button onClick={onClose} data-testid="reset-modal-close" className="p-2 border border-zinc-800 hover:border-white">
            <X size={14} />
          </button>
        </div>
        <div className="text-sm">
          <div className="text-zinc-300">{user.nickname}</div>
          <div className="text-xs text-zinc-500">{user.email}</div>
        </div>

        {!result ? (
          <>
            <div className="space-y-2 text-xs">
              <label className="flex items-center gap-2 cursor-pointer p-3 border border-zinc-800 hover:border-zinc-700">
                <input type="radio" checked={mode === "auto"} onChange={() => setMode("auto")} data-testid="reset-mode-auto" />
                <span>Generar contraseña temporal segura (recomendado)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-3 border border-zinc-800 hover:border-zinc-700">
                <input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} data-testid="reset-mode-manual" />
                <span>Definir manualmente</span>
              </label>
              {mode === "manual" && (
                <input
                  type="text"
                  value={manualPw}
                  onChange={(e) => setManualPw(e.target.value)}
                  data-testid="reset-manual-input"
                  placeholder="Nueva contraseña (mín 4)"
                  className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] outline-none px-3 py-2 text-sm rounded-sm font-mono"
                  minLength={4}
                  autoFocus
                />
              )}
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-3">
              ⚠️ Esta acción cierra todas las sesiones activas del usuario y le exige iniciar sesión otra vez con la nueva contraseña.
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-wider">Cancelar</button>
              <button
                onClick={submit}
                disabled={busy}
                data-testid="reset-submit"
                className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50"
              >
                {busy ? "Restableciendo..." : "Restablecer"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-black border border-[var(--c-lime)] p-4 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">Nueva contraseña</div>
              <div data-testid="reset-result-password" className="font-mono text-xl text-[var(--c-lime)] break-all select-all">
                {result.new_password}
              </div>
              <button
                onClick={copyPassword}
                data-testid="reset-copy-button"
                className={`w-full mt-2 px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${copied ? "border-[var(--c-lime)] text-[var(--c-lime)]" : "border-zinc-700 text-zinc-300 hover:border-white hover:text-white"}`}
              >
                {copied ? "✓ Copiado" : "Copiar al portapapeles"}
              </button>
            </div>
            <div className="text-[11px] text-zinc-400 leading-relaxed border-t border-zinc-800 pt-3 space-y-1">
              <div>📩 Cópiala AHORA — no podrás verla de nuevo después de cerrar este diálogo.</div>
              <div>Compártela con <strong className="text-white">{user.email}</strong> por un canal seguro (WhatsApp, email).</div>
            </div>
            <button
              onClick={onClose}
              data-testid="reset-done"
              className="w-full bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider py-2 text-xs"
            >
              Listo
            </button>
          </>
        )}
      </div>
    </div>
  );
}



const DEFAULT_HERO = {
  kicker: { es: "FIFA WORLD CUP 2026", en: "FIFA WORLD CUP 2026" },
  lines: [
    { text: { es: "Pronostica tus resultados,", en: "Predict your results," }, color: "#FFFFFF" },
    { text: { es: "acierta los marcadores,", en: "guess the scores," }, color: "var(--c-lime)" },
    { text: { es: "sube en la tabla de posiciones", en: "climb the leaderboard" }, color: "var(--c-sky)" },
    { text: { es: "y gánale a tus amigos y familiares.", en: "and beat your friends and family." }, color: "var(--c-pink)" },
  ],
};

const HERO_COLOR_OPTIONS = [
  { value: "#FFFFFF", label: "Blanco" },
  { value: "var(--c-lime)", label: "Lima" },
  { value: "var(--c-sky)", label: "Celeste" },
  { value: "var(--c-pink)", label: "Rosa" },
  { value: "var(--c-yellow)", label: "Amarillo" },
  { value: "var(--c-peach)", label: "Durazno" },
  { value: "var(--c-purple)", label: "Morado" },
  { value: "var(--c-red)", label: "Rojo" },
];

function HomePagePanel() {
  const [kicker, setKicker] = useState(DEFAULT_HERO.kicker);
  const [lines, setLines] = useState(DEFAULT_HERO.lines);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const lineIdRef = React.useRef(0);
  const newLineId = () => `line_${++lineIdRef.current}`;

  const withId = (l) => ({ _id: newLineId(), ...l });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/page-content");
      const h = r.data?.login_hero || DEFAULT_HERO;
      setKicker(toBilingual(h.kicker || DEFAULT_HERO.kicker));
      const incoming = Array.isArray(h.lines) && h.lines.length > 0 ? h.lines : DEFAULT_HERO.lines;
      setLines(incoming.map((l) => withId({ text: toBilingual(l.text), color: l.color || "#FFFFFF" })));
    } catch {
      // Page content might not exist yet on first load: keep defaults.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!(kicker.es || "").trim()) { toast.error("El texto superior en español no puede estar vacío"); return; }
    if (lines.length === 0) { toast.error("Debe haber al menos una línea"); return; }
    if (lines.some((l) => !(l.text?.es || "").trim())) { toast.error("Cada línea debe tener texto en español"); return; }
    setBusy(true);
    try {
      await api.put("/admin/page-content", {
        login_hero: {
          kicker: { es: kicker.es.trim(), en: (kicker.en || "").trim() },
          lines: lines.map((l) => ({
            text: { es: (l.text.es || "").trim(), en: (l.text.en || "").trim() },
            color: l.color,
          })),
        },
      });
      toast.success("Página de inicio actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const restoreDefaults = () => {
    if (!confirm("¿Restaurar el mensaje de bienvenida a los valores por defecto?")) return;
    setKicker(DEFAULT_HERO.kicker);
    setLines(DEFAULT_HERO.lines.map((l) => withId({ ...l })));
  };

  const updateLine = (i, patch) => {
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  };
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const addLine = () => setLines((prev) => [...prev, withId({ text: { es: "Nueva línea", en: "" }, color: "#FFFFFF" })]);
  const moveLine = (i, dir) => {
    setLines((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  if (loading) return <div className="text-zinc-500 text-sm">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="home-page-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-5">
        <h2 className="font-display text-xl uppercase mb-2">Mensaje de bienvenida</h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Edita el texto pequeño superior ("FIFA WORLD CUP 2026") y las líneas de colores que aparecen en la
          página de inicio (login). Edita ambos idiomas de forma independiente; usa el botón con icono de idiomas
          para sugerir una traducción con IA (puedes ajustarla después).
        </p>
        <p className="text-zinc-500 text-xs mt-2">
          Para mostrar/ocultar este bloque (o usar una imagen), ve a <strong className="text-white">Fondos de secciones</strong>.
        </p>
      </div>

      {/* Kicker */}
      <div className="bg-zinc-900 border border-zinc-800 p-5 space-y-2">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
          Texto pequeño superior (kicker)
        </label>
        <BilingualEditor
          value={kicker}
          onChange={setKicker}
          placeholderEs="Texto superior (ES)"
          placeholderEn="Texto superior (EN)"
          testidPrefix="hero-kicker"
        />
      </div>

      {/* Lines repeater */}
      <div className="bg-zinc-900 border border-zinc-800 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
            Líneas del mensaje (con color por línea)
          </label>
          <button
            onClick={addLine}
            data-testid="hero-add-line"
            className="flex items-center gap-1 text-xs uppercase tracking-widest text-[var(--c-lime)] hover:text-white"
          >
            <Plus size={12} /> Añadir línea
          </button>
        </div>
        {lines.map((line, i) => (
          <div key={line._id || `line-${i}`} className="bg-black border border-zinc-800 p-3 space-y-2" data-testid={`hero-line-${i}`}>
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex gap-1">
                <button onClick={() => moveLine(i, -1)} disabled={i === 0}
                  data-testid={`hero-up-${i}`}
                  className="w-7 h-7 border border-zinc-800 hover:border-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-xs">↑</button>
                <button onClick={() => moveLine(i, +1)} disabled={i === lines.length - 1}
                  data-testid={`hero-down-${i}`}
                  className="w-7 h-7 border border-zinc-800 hover:border-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-xs">↓</button>
              </div>
              <select
                value={line.color}
                onChange={(e) => updateLine(i, { color: e.target.value })}
                data-testid={`hero-line-color-${i}`}
                className="bg-zinc-950 border border-zinc-800 outline-none px-2 py-2 text-xs"
                style={{ color: line.color }}
              >
                {HERO_COLOR_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value} style={{ color: "#fff", background: "#000" }}>{c.label}</option>
                ))}
              </select>
              <button
                onClick={() => removeLine(i)}
                data-testid={`hero-remove-${i}`}
                className="w-7 h-7 border border-[var(--c-red)] text-[var(--c-red)] hover:bg-[var(--c-red)] hover:text-black flex items-center justify-center ml-auto"
                title="Eliminar línea"
              >
                <Trash2 size={12} />
              </button>
            </div>
            <BilingualEditor
              value={line.text}
              onChange={(next) => updateLine(i, { text: next })}
              placeholderEs="Texto (ES)"
              placeholderEn="Texto (EN)"
              testidPrefix={`hero-line-${i}-text`}
            />
          </div>
        ))}
      </div>

      {/* Preview */}
      <div className="bg-black border border-zinc-800 p-6 md:p-10 text-center" data-testid="hero-preview">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-3">Vista previa · ES (los usuarios verán el idioma de su selección)</div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-4">{kicker.es || kicker.en}</div>
        <div className="font-display text-2xl sm:text-3xl md:text-4xl uppercase leading-[1.1]">
          {lines.map((line, i) => (
            <span key={line._id || `preview-${i}`} style={{ color: line.color }}>
              {line.text?.es || line.text?.en}{i < lines.length - 1 && <br />}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={save}
          disabled={busy}
          data-testid="hero-save-button"
          className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={14} /> Guardar mensaje
        </button>
        <button
          onClick={restoreDefaults}
          disabled={busy}
          data-testid="hero-restore-defaults"
          className="border border-zinc-700 text-zinc-300 hover:text-white hover:border-white font-bold uppercase tracking-wider px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50"
        >
          <RotateCcw size={14} /> Restaurar valores por defecto
        </button>
      </div>
    </div>
  );
}



function PublishPanel({ appState, onChange }) {
  const STAGES = [
    { id: "groups", label: "Fase 1 — Grupos & Picks de Torneo", short: "Fase 1" },
    { id: "r32", label: "16vos de final", short: "16vos" },
    { id: "r16", label: "Octavos de final", short: "8vos" },
    { id: "qf", label: "Cuartos de final", short: "4tos" },
    { id: "sf", label: "Semifinal", short: "Semis" },
    { id: "third", label: "Tercer puesto", short: "3er Puesto" },
    { id: "final", label: "Final", short: "Final" },
  ];
  const [busy, setBusy] = useState(null);
  const togglePublish = async (stageId, publish) => {
    const stage = STAGES.find((s) => s.id === stageId);
    const action = publish ? "publicar" : "despublicar";
    const msg = publish
      ? `¿Publicar pronósticos de "${stage.label}"? Después de publicar, NADIE podrá modificar sus predicciones de esta etapa.`
      : `¿Despublicar "${stage.label}"? Los usuarios podrán volver a modificar sus pronósticos de esta etapa.`;
    if (!confirm(msg)) return;
    setBusy(stageId);
    try {
      const endpoint = publish ? `/admin/publish-stage/${stageId}` : `/admin/unpublish-stage/${stageId}`;
      await api.post(endpoint);
      toast.success(`${publish ? "Publicado" : "Despublicado"}: ${stage.label}`);
      onChange();
    } catch (e) {
      toast.error(`Error al ${action}: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(null); }
  };

  const stages = appState.stages || {};

  return (
    <div className="space-y-4" data-testid="publish-panel">
      <div className="text-sm text-zinc-300 leading-relaxed">
        Publica los pronósticos por etapa de forma independiente. Cuando publicas una etapa,
        los pronósticos de esa etapa quedan visibles para todos en <strong className="text-white">Todos los Pronósticos</strong>,
        habilitas la descarga del Excel correspondiente y los usuarios ya no pueden modificarlos.
        Cada acción es reversible mediante el botón "Despublicar".
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {STAGES.map((s) => {
          const published = !!stages[s.id];
          return (
            <div
              key={s.id}
              className={`p-4 border ${
                published ? "border-[var(--c-lime)] bg-[var(--c-lime)]/10" : "border-zinc-800 bg-zinc-900"
              }`}
              data-testid={`publish-card-${s.id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                {published
                  ? <Lock size={20} className="text-[var(--c-lime)] mt-0.5" />
                  : <Unlock size={20} className="text-zinc-500 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase leading-tight">{s.label}</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    {published ? "Publicado · pronósticos bloqueados" : "Editable · usuarios pueden modificar"}
                  </div>
                </div>
              </div>
              {!published ? (
                <button
                  onClick={() => togglePublish(s.id, true)}
                  disabled={busy === s.id}
                  data-testid={`publish-button-${s.id}`}
                  className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50"
                >
                  <Send size={12} /> Publicar
                </button>
              ) : (
                <button
                  onClick={() => togglePublish(s.id, false)}
                  disabled={busy === s.id}
                  data-testid={`unpublish-button-${s.id}`}
                  className="border border-[var(--c-red)] text-[var(--c-red)] font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50"
                >
                  <Unlock size={12} /> Despublicar
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-xs text-zinc-500 font-mono-data leading-relaxed">
        <strong className="text-zinc-400">NOTA:</strong> Cada etapa se publica de forma independiente.
        Por ejemplo, puedes publicar Fase 1 cuando termine la fase de grupos y mantener bloqueada
        la descarga de "16vos" hasta que ese partido esté por comenzar. El estado es reversible en cualquier momento.
      </div>
    </div>
  );
}

function KOSetup({ matches, teams, teamByCode, onChange }) {
  const koStages = [
    { id: "r32", label: "16vos de final" },
    { id: "r16", label: "Octavos de final" },
    { id: "qf", label: "Cuartos de final" },
    { id: "sf", label: "Semifinal" },
    { id: "third", label: "Tercer puesto" },
    { id: "final", label: "Final" },
  ];
  const ko = matches.filter((m) => m.stage !== "group");
  const totalSet = ko.filter((m) => m.home && m.away).length;
  const sortedTeams = [...teams].sort((a, b) => a.name.localeCompare(b.name));

  const update = async (mid, field, val) => {
    try {
      await api.put(`/admin/match/${mid}`, { [field]: val || null });
      toast.success("Actualizado");
      onChange();
    } catch {
      toast.error("Error");
    }
  };

  const resetMatch = async (m) => {
    if (!confirm(`¿Resetear el partido ${m.match_id}? Se borrarán equipos, marcador y ganador.`)) return;
    try {
      // Clear result first
      await api.delete(`/admin/match/${m.match_id}/result`);
      // Then clear team assignments
      await api.put(`/admin/match/${m.match_id}`, { home: null, away: null });
      toast.success(`${m.match_id} reseteado`);
      onChange();
    } catch {
      toast.error("No se pudo resetear");
    }
  };

  return (
    <div className="space-y-4" data-testid="ko-setup-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-lg uppercase">Definir eliminatorias</div>
          <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
            Selecciona el equipo Local y Visitante que clasificaron a cada llave. Aquí no se ingresa el marcador — eso se hace en la pestaña <strong className="text-white">Resultados de Partido</strong> una vez se haya jugado el partido real.
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-data text-2xl font-bold text-[var(--c-lime)]">{totalSet}/{ko.length}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">DEFINIDOS</div>
        </div>
      </div>
      {koStages.map((s) => {
        const stageMatches = ko.filter((m) => m.stage === s.id).sort((a, b) => a.match_id.localeCompare(b.match_id));
        return (
          <div key={s.id} className="bg-zinc-900 border border-zinc-800">
            <div className="px-4 py-2 border-b border-zinc-800 font-display text-sm uppercase">{s.label}</div>
            {stageMatches.map((m) => (
              <KOMatchEditor
                key={m.match_id}
                m={m}
                sortedTeams={sortedTeams}
                onUpdate={update}
                onReset={() => resetMatch(m)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function KOMatchEditor({ m, sortedTeams, onUpdate, onReset }) {
  const teamsSet = !!m.home && !!m.away;
  return (
    <div className="px-4 py-3 border-b border-zinc-800 flex flex-col gap-2 text-xs" data-testid={`ko-setup-${m.match_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-mono-data text-zinc-500 w-16">{m.match_id}</div>
        <div className="font-mono-data text-zinc-600 w-20">slot: {m.home_slot}/{m.away_slot}</div>
        <select value={m.home || ""} onChange={(e) => onUpdate(m.match_id, "home", e.target.value)}
          data-testid={`ko-setup-home-${m.match_id}`}
          className="bg-black border border-zinc-800 px-2 py-1 text-xs flex-1 min-w-[140px]">
          <option value="">— Local —</option>
          {sortedTeams.map((t) => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
        </select>
        <span className="text-zinc-600">vs</span>
        <select value={m.away || ""} onChange={(e) => onUpdate(m.match_id, "away", e.target.value)}
          data-testid={`ko-setup-away-${m.match_id}`}
          className="bg-black border border-zinc-800 px-2 py-1 text-xs flex-1 min-w-[140px]">
          <option value="">— Visitante —</option>
          {sortedTeams.map((t) => <option key={t.code} value={t.code}>{t.code} · {t.name}</option>)}
        </select>
        <button
          onClick={onReset}
          data-testid={`ko-reset-${m.match_id}`}
          className="p-2 border border-[var(--c-red)] text-[var(--c-red)] hover:bg-[var(--c-red)] hover:text-black"
          title="Resetear equipos del partido"
        >
          <RotateCcw size={12} />
        </button>
      </div>
      {teamsSet && (
        <div className="pl-16 text-[10px] text-zinc-500 font-mono-data flex items-center gap-2">
          <span className="text-[var(--c-lime)]">●</span>
          Equipos definidos. El marcador final se registra en la pestaña <strong className="text-white">Resultados de Partido</strong>.
        </div>
      )}
    </div>
  );
}


function InviteCodes() {
  const [codes, setCodes] = useState([]);
  const [pools, setPools] = useState([]);
  const [customCode, setCustomCode] = useState("");
  const [label, setLabel] = useState("");
  const [poolId, setPoolId] = useState("pool_general");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/admin/invite-codes"),  // default backend filter = Mundial only
      api.get("/admin/pools"),
    ]);
    setCodes(c.data);
    setPools(p.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post("/admin/invite-codes", {
        code: customCode.trim().toUpperCase() || null,
        label: label.trim() || null,
        pool_id: poolId,
      });
      setCustomCode(""); setLabel("");
      toast.success("Código creado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const remove = async (code) => {
    if (!confirm(`¿Eliminar código ${code}?`)) return;
    try {
      await api.delete(`/admin/invite-codes/${code}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copiado: ${code}`);
  };

  const poolName = (pid) => pools.find((p) => p.pool_id === pid)?.name || pid;

  const unusedCount = codes.filter((c) => !c.used_by_user_id).length;
  const usedCount = codes.length - unusedCount;

  return (
    <div className="space-y-4" data-testid="invites-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="flex items-center gap-3 mb-3">
          <KeyRound className="text-[var(--c-yellow)]" size={20} />
          <div className="font-display text-lg uppercase">Crear código de invitación</div>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Genera un código único que el participante deberá ingresar al registrarse. Cada código solo puede usarse una vez. El código puede dar acceso a una polla específica.
        </p>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Código (opcional)</label>
            <input value={customCode} onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
              data-testid="invite-custom-code"
              placeholder="Auto-generar"
              className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] font-mono-data uppercase tracking-wider rounded-sm" maxLength={20} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Polla destino</label>
            <select value={poolId} onChange={(e) => setPoolId(e.target.value)} data-testid="invite-pool-select"
              className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] rounded-sm">
              {pools.map((p) => <option key={p.pool_id} value={p.pool_id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Nota</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              data-testid="invite-label"
              placeholder="Ej. Juan Pérez"
              className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] rounded-sm" maxLength={100} />
          </div>
          <button type="submit" disabled={busy} data-testid="invite-create-button"
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center justify-center gap-2 h-10 disabled:opacity-50">
            <Plus size={14} /> Crear
          </button>
        </form>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Total" value={codes.length} color="white" />
        <Stat label="Disponibles" value={unusedCount} color="var(--c-lime)" />
        <Stat label="Usados" value={usedCount} color="var(--c-peach)" />
      </div>

      <InviteCodesTable codes={codes} poolName={poolName} pools={pools} onCopy={copy} onRemove={remove} />
    </div>
  );
}

function InviteCodesTable({ codes, poolName, pools, onCopy, onRemove }) {
  const [sortBy, setSortBy] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const [filterStatus, setFilterStatus] = useState("all"); // all | available | used
  const [filterPool, setFilterPool] = useState("all");
  const [filterQuery, setFilterQuery] = useState("");

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    return codes.filter((c) => {
      if (filterStatus === "available" && c.used_by_user_id) return false;
      if (filterStatus === "used" && !c.used_by_user_id) return false;
      if (filterPool !== "all" && (c.pool_id || "pool_general") !== filterPool) return false;
      if (q) {
        const haystack = [
          c.code, c.label || "", c.used_by_email || "",
          poolName(c.pool_id || "pool_general"),
        ].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [codes, filterStatus, filterPool, filterQuery, poolName]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    const keyFns = {
      code: (c) => (c.code || "").toLowerCase(),
      pool: (c) => poolName(c.pool_id || "pool_general").toLowerCase(),
      status: (c) => (c.used_by_user_id ? 1 : 0),
      used_by: (c) => (c.used_by_email || "~").toLowerCase(),
    };
    const fn = keyFns[sortBy] || keyFns.code;
    arr.sort((a, b) => {
      const va = fn(a); const vb = fn(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortBy, sortDir, poolName]);

  const SortHeader = ({ id, label, className }) => (
    <button
      onClick={() => toggleSort(id)}
      data-testid={`invite-sort-${id}`}
      className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-data hover:text-white transition-colors ${
        sortBy === id ? "text-[var(--c-lime)]" : "text-zinc-500"
      } ${className || ""}`}
    >
      <span>{label}</span>
      {sortBy === id && (
        sortDir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />
      )}
    </button>
  );

  return (
    <>
      {/* Filters row */}
      <div className="bg-zinc-900 border border-zinc-800 p-3 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="invite-filters">
        <input
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          data-testid="invite-filter-query"
          placeholder="Buscar código, nota o email…"
          className="bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] text-xs rounded-sm"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="invite-filter-status"
          className="bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] text-xs rounded-sm"
        >
          <option value="all">Estado: Todos</option>
          <option value="available">Solo disponibles</option>
          <option value="used">Solo usados</option>
        </select>
        <select
          value={filterPool}
          onChange={(e) => setFilterPool(e.target.value)}
          data-testid="invite-filter-pool"
          className="bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] text-xs rounded-sm"
        >
          <option value="all">Polla: Todas</option>
          {pools.map((p) => (
            <option key={p.pool_id} value={p.pool_id}>{p.name}</option>
          ))}
        </select>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data self-center text-right md:text-left">
          Mostrando <span className="text-white font-bold">{sorted.length}</span> de {codes.length}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800">
          <div className="col-span-2"><SortHeader id="code" label="Código" /></div>
          <div className="col-span-2"><SortHeader id="pool" label="Polla" /></div>
          <div className="col-span-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">Nota</div>
          <div className="col-span-2"><SortHeader id="status" label="Estado" /></div>
          <div className="col-span-3"><SortHeader id="used_by" label="Usado por" /></div>
          <div className="col-span-1 text-right text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">Acción</div>
        </div>
        {sorted.length === 0 && (
          <div className="px-4 py-6 text-center text-zinc-500 text-sm">
            {codes.length === 0 ? "Aún no hay códigos. Crea uno arriba." : "Ningún código coincide con los filtros."}
          </div>
        )}
        {sorted.map((c) => (
          <div key={c.code} data-testid={`invite-row-${c.code}`}
            className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 items-center text-sm">
            <div className="col-span-2 font-mono-data font-bold tracking-wider text-[var(--c-yellow)]">{c.code}</div>
            <div className="col-span-2 text-xs text-zinc-300">{poolName(c.pool_id || "pool_general")}</div>
            <div className="col-span-2 text-zinc-400 truncate">{c.label || <span className="text-zinc-600">—</span>}</div>
            <div className="col-span-2">
              {c.used_by_user_id ? (
                <span className="text-[10px] font-mono-data uppercase tracking-widest text-[var(--c-peach)]">USADO</span>
              ) : (
                <span className="text-[10px] font-mono-data uppercase tracking-widest text-[var(--c-lime)]">DISPONIBLE</span>
              )}
            </div>
            <div className="col-span-3 text-xs text-zinc-400 truncate">
              {c.used_by_email ? <>{c.used_by_email}<br/><span className="text-[10px] text-zinc-600 font-mono-data">{new Date(c.used_at).toLocaleString("es-MX")}</span></> : <span className="text-zinc-600">—</span>}
            </div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button onClick={() => onCopy(c.code)} data-testid={`invite-copy-${c.code}`}
                className="p-1.5 border border-zinc-800 hover:border-white" title="Copiar">
                <Copy size={12} />
              </button>
              {!c.used_by_user_id && (
                <button onClick={() => onRemove(c.code)} data-testid={`invite-delete-${c.code}`}
                  className="p-1.5 border border-zinc-800 hover:border-[var(--c-red)] text-[var(--c-red)]" title="Eliminar">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 py-4">
      <div className="font-display text-3xl" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{label}</div>
    </div>
  );
}



function SettingsPanel() {
  const [s, setS] = useState({ social_instagram: "", social_facebook: "", social_tiktok: "", social_youtube: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS({
      social_instagram: r.data.social_instagram || "",
      social_facebook: r.data.social_facebook || "",
      social_tiktok: r.data.social_tiktok || "",
      social_youtube: r.data.social_youtube || "",
    })).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error al guardar");
    } finally { setBusy(false); }
  };

  const fields = [
    { key: "social_instagram", label: "Instagram", placeholder: "https://instagram.com/lapollamundialista", color: "var(--c-pink)" },
    { key: "social_facebook", label: "Facebook", placeholder: "https://facebook.com/lapollamundialista", color: "var(--c-blue)" },
    { key: "social_tiktok", label: "TikTok", placeholder: "https://tiktok.com/@lapollamundialista", color: "var(--c-mint)" },
    { key: "social_youtube", label: "YouTube", placeholder: "https://youtube.com/@lapollamundialista", color: "var(--c-red)" },
  ];

  return (
    <div className="space-y-4" data-testid="settings-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="flex items-center gap-3 mb-3">
          <Settings className="text-[var(--c-lime)]" size={20} />
          <div className="font-display text-lg uppercase">Enlaces de redes sociales</div>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Pega aquí las URLs completas de las redes sociales de La Polla Mundialista. Aparecerán como íconos clicables en el pie de página de toda la app. Deja vacío para ocultar.
        </p>
        <div className="space-y-3">
          {fields.map(({ key, label, placeholder, color }) => (
            <div key={key}>
              <label className="block text-[10px] uppercase tracking-widest font-mono-data mb-1" style={{ color }}>{label}</label>
              <input
                type="url"
                value={s[key]}
                onChange={(e) => setS({ ...s, [key]: e.target.value })}
                placeholder={placeholder}
                data-testid={`settings-${key}`}
                className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-lime)] rounded-sm font-mono-data text-xs"
              />
            </div>
          ))}
        </div>
        <button onClick={save} disabled={busy} data-testid="settings-save"
          className="mt-5 bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-6 py-3 text-xs disabled:opacity-50 flex items-center gap-2">
          <Save size={14} /> Guardar enlaces
        </button>
      </div>
    </div>
  );
}



function PoolPrizeEditor({ pool, onSaved }) {
  const initial = pool.prize || { amount_text: "", percent_first: 65, percent_second: 25, percent_third: 10 };
  const [amount, setAmount] = useState(initial.amount_text || "");
  const [p1, setP1] = useState(initial.percent_first ?? 65);
  const [p2, setP2] = useState(initial.percent_second ?? 25);
  const [p3, setP3] = useState(initial.percent_third ?? 10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const pr = pool.prize || {};
    setAmount(pr.amount_text || "");
    setP1(pr.percent_first ?? 65);
    setP2(pr.percent_second ?? 25);
    setP3(pr.percent_third ?? 10);
  }, [pool.pool_id, pool.prize]);

  const total = Number(p1 || 0) + Number(p2 || 0) + Number(p3 || 0);
  const invalid = total > 100;

  const save = async () => {
    if (invalid) { toast.error(`Los % suman ${total}% (máx 100)`); return; }
    setSaving(true);
    try {
      await api.put(`/admin/pools/${pool.pool_id}/prize`, {
        amount_text: amount.trim(),
        percent_first: Number(p1 || 0),
        percent_second: Number(p2 || 0),
        percent_third: Number(p3 || 0),
      });
      toast.success(amount.trim() ? "Premio guardado" : "Premio eliminado");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/pools/${pool.pool_id}/prize`, { amount_text: "" });
      setAmount("");
      toast.success("Premio eliminado");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-black/40 border border-zinc-800 p-3 space-y-2" data-testid={`pool-prize-${pool.pool_id}`}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--c-mint)] font-mono-data">Premio acumulado</div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Monto (texto)</label>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`pool-prize-amount-${pool.pool_id}`}
            placeholder='$1.500.000 COP'
            maxLength={80}
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-mint)] rounded-sm" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">% 1ro</label>
          <input type="number" min={0} max={100} value={p1} onChange={(e) => setP1(e.target.value)} data-testid={`pool-prize-p1-${pool.pool_id}`}
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-yellow)] rounded-sm" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">% 2do</label>
          <input type="number" min={0} max={100} value={p2} onChange={(e) => setP2(e.target.value)} data-testid={`pool-prize-p2-${pool.pool_id}`}
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-zinc-300 rounded-sm" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">% 3ro</label>
          <input type="number" min={0} max={100} value={p3} onChange={(e) => setP3(e.target.value)} data-testid={`pool-prize-p3-${pool.pool_id}`}
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-peach)] rounded-sm" />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className={`text-[10px] font-mono-data ${invalid ? "text-[var(--c-red)]" : "text-zinc-500"}`}>
          Suma: {total}% {invalid ? "(excede 100%)" : ""}
        </div>
        <div className="flex gap-2">
          {pool.prize && (
            <button onClick={clear} disabled={saving} data-testid={`pool-prize-clear-${pool.pool_id}`}
              className="border border-[var(--c-red)] text-[var(--c-red)] px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-[var(--c-red)] hover:text-black disabled:opacity-50">
              Quitar premio
            </button>
          )}
          <button onClick={save} disabled={saving || invalid} data-testid={`pool-prize-save-${pool.pool_id}`}
            className="bg-[var(--c-mint)] text-black font-bold uppercase tracking-wider px-3 py-1.5 text-[10px] disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar premio"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SpecialPoolsPanel() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/special-pools");
      setPools(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api.post("/admin/special-pools", {});
      toast.success("Polla especial creada — completa los datos y guarda");
      await load();
      setExpandedId(r.data.special_pool_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (sp) => {
    if (!confirm(`¿Eliminar polla especial "${sp.home_team_name} vs ${sp.away_team_name}"? Esto borra todos los pronósticos asociados.`)) return;
    try {
      await api.delete(`/admin/special-pools/${sp.special_pool_id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-4" data-testid="special-pools-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-xl uppercase">Pollas Especiales</div>
          <p className="text-zinc-400 text-sm">Crea pollas para partidos sueltos (clásicos, finales de otras ligas, amistosos, etc.).</p>
        </div>
        <button onClick={create} data-testid="special-pool-create"
          className="bg-[var(--c-yellow)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs">
          + Nueva polla especial
        </button>
      </div>
      {loading && <div className="text-zinc-400 text-sm">Cargando…</div>}
      {pools.length === 0 && !loading && (
        <div className="bg-zinc-900 border border-zinc-800 p-8 text-center text-zinc-500">
          Aún no hay pollas especiales. Crea la primera.
        </div>
      )}
      {pools.map((sp) => (
        <SpecialPoolCard key={sp.special_pool_id} sp={sp}
          expanded={expandedId === sp.special_pool_id}
          onToggle={() => setExpandedId(expandedId === sp.special_pool_id ? null : sp.special_pool_id)}
          onChange={load}
          onDelete={() => remove(sp)} />
      ))}
    </div>
  );
}

function SpecialPoolCard({ sp, expanded, onToggle, onChange, onDelete }) {
  const titleES = (sp.title && sp.title.es) || (typeof sp.title === "string" ? sp.title : "Pollas Especiales");
  const titleEN = (sp.title && sp.title.en) || titleES;
  const [titleEs, setTitleEs] = useState(titleES);
  const [titleEn, setTitleEn] = useState(titleEN);
  const [home, setHome] = useState(sp.home_team_name || "");
  const [away, setAway] = useState(sp.away_team_name || "");
  const [homeImg, setHomeImg] = useState(sp.home_team_image_url || "");
  const [awayImg, setAwayImg] = useState(sp.away_team_image_url || "");
  const [kickoff, setKickoff] = useState((sp.kickoff || "").slice(0, 16));
  const [venue, setVenue] = useState(sp.venue || "");
  const [locked, setLocked] = useState(!!sp.locked);
  const [resultHome, setResultHome] = useState(sp.result_home ?? "");
  const [resultAway, setResultAway] = useState(sp.result_away ?? "");
  const [resultPublished, setResultPublished] = useState(!!sp.result_published);
  const [pdfUrl, setPdfUrl] = useState(sp.rules_pdf_url || "");
  const [prizeAmount, setPrizeAmount] = useState(sp.prize?.amount_text || "");

  const save = async () => {
    if (!home.trim() || !away.trim()) {
      toast.error("Ingresa el nombre del equipo local y visitante");
      return;
    }
    try {
      const payload = {
        title: { es: titleEs.trim() || "Pollas Especiales", en: titleEn.trim() || titleEs.trim() || "Special Pools" },
        home_team_name: home.trim(),
        away_team_name: away.trim(),
        home_team_image_url: homeImg || null,
        away_team_image_url: awayImg || null,
        kickoff: kickoff ? new Date(kickoff).toISOString() : null,
        venue: venue.trim() || null,
        locked,
        rules_pdf_url: pdfUrl || null,
        result_home: resultHome === "" ? null : Number(resultHome),
        result_away: resultAway === "" ? null : Number(resultAway),
        result_published: resultPublished,
        prize: prizeAmount.trim() ? { amount_text: prizeAmount.trim() } : null,
      };
      await api.put(`/admin/special-pools/${sp.special_pool_id}`, payload);
      toast.success("Polla especial actualizada");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const uploadTeamImage = async (side, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (side === "home") setHomeImg(r.data.url); else setAwayImg(r.data.url);
      toast.success("Imagen subida — recuerda Guardar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir imagen");
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Solo PDF"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdfUrl(r.data.url);
      toast.success("PDF subido — recuerda Guardar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir PDF");
    }
  };

  const headerLabel = home && away
    ? `${home} vs ${away}`
    : (titleES && titleES !== "Pollas Especiales" ? titleES : "Sin configurar");

  return (
    <div className="bg-zinc-900 border border-zinc-800" data-testid={`special-pool-card-${sp.special_pool_id}`}>
      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg uppercase truncate">{headerLabel}</div>
          <div className="text-xs text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
            <span>{sp.member_count || 0} miembros</span>
            {locked && <span className="text-[var(--c-peach)]">🔒 Bloqueada</span>}
            {resultPublished && <span className="text-[var(--c-lime)]">✓ Resultado publicado</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onToggle} data-testid={`special-pool-toggle-${sp.special_pool_id}`}
            className="px-3 py-2 text-[10px] uppercase tracking-widest border border-zinc-800 hover:border-white">
            {expanded ? "Cerrar" : "Editar"}
          </button>
          <button onClick={onDelete} data-testid={`special-pool-delete-${sp.special_pool_id}`}
            className="p-2 border border-zinc-800 hover:border-[var(--c-red)] text-[var(--c-red)]">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Título (Español)" value={titleEs} onChange={setTitleEs} placeholder="Pollas Especiales" testid={`sp-title-es-${sp.special_pool_id}`} />
            <Field label="Title (English)" value={titleEn} onChange={setTitleEn} placeholder="Special Pools" testid={`sp-title-en-${sp.special_pool_id}`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TeamEditor side="home" name={home} setName={setHome} img={homeImg} onUpload={(f) => uploadTeamImage("home", f)} sp={sp} />
            <TeamEditor side="away" name={away} setName={setAway} img={awayImg} onUpload={(f) => uploadTeamImage("away", f)} sp={sp} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Fecha y hora del partido</label>
              <input type="datetime-local" value={kickoff} onChange={(e) => setKickoff(e.target.value)}
                className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] rounded-sm text-sm" />
            </div>
            <Field label="Sede" value={venue} onChange={setVenue} placeholder="Bernabéu" />
          </div>

          {/* Prize */}
          <div className="bg-black/40 border border-zinc-800 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[var(--c-mint)] font-mono-data">Premio</div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Monto del premio</label>
              <input value={prizeAmount} onChange={(e) => setPrizeAmount(e.target.value)} placeholder="$200 USD" maxLength={80}
                data-testid={`sp-prize-amount-${sp.special_pool_id}`}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-mint)] rounded-sm" />
              <div className="text-[10px] text-zinc-500 mt-1">
                El premio se reparte en partes iguales entre los usuarios que acierten el marcador exacto.
              </div>
            </div>
          </div>

          {/* PDF */}
          <div className="bg-black/40 border border-zinc-800 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[var(--c-lime)] font-mono-data">Reglamento PDF</div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-zinc-700 hover:border-[var(--c-lime)] px-3 py-2 text-xs uppercase tracking-wider text-zinc-300">
                Subir PDF
                <input type="file" accept="application/pdf" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadPdf(f); }} />
              </label>
              {pdfUrl && (
                <>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--c-lime)] underline truncate max-w-xs">{pdfUrl}</a>
                  <button onClick={() => setPdfUrl("")} className="text-xs text-[var(--c-red)] border border-[var(--c-red)] px-2 py-1 uppercase tracking-wider">Quitar</button>
                </>
              )}
            </div>
          </div>

          {/* Lock + Result */}
          <div className="bg-black/40 border border-zinc-800 p-3 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} data-testid={`sp-lock-${sp.special_pool_id}`} />
              <div>
                <div className="text-sm font-semibold">🔒 Bloquear edición de pronósticos</div>
                <div className="text-xs text-zinc-400">Al activar, los usuarios verán los pronósticos de los demás en la pestaña 2.</div>
              </div>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Resultado local</label>
                <input type="number" min="0" max="99" value={resultHome} onChange={(e) => setResultHome(e.target.value)} data-testid={`sp-result-home-${sp.special_pool_id}`}
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-yellow)] rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Resultado visitante</label>
                <input type="number" min="0" max="99" value={resultAway} onChange={(e) => setResultAway(e.target.value)} data-testid={`sp-result-away-${sp.special_pool_id}`}
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-yellow)] rounded-sm" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={resultPublished} onChange={(e) => setResultPublished(e.target.checked)} data-testid={`sp-publish-${sp.special_pool_id}`} />
                <span className="text-xs uppercase tracking-wider">Publicar ganadores</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={save} data-testid={`sp-save-${sp.special_pool_id}`}
              className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs">
              Guardar cambios
            </button>
          </div>

          {/* Invite codes scoped to this special pool */}
          <SpecialPoolInviteCodes sp={sp} />
        </div>
      )}
    </div>
  );
}

function SpecialPoolInviteCodes({ sp }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customCode, setCustomCode] = useState("");
  const matchLabel = `${sp.home_team_name} vs ${sp.away_team_name}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invite-codes", { params: { special_pool_id: sp.special_pool_id } });
      setCodes(r.data || []);
    } catch (e) {
      // surface failures so the admin doesn't see a silently-empty list
      toast.error(e?.response?.data?.detail || "Error al cargar códigos");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [sp.special_pool_id]);  // eslint-disable-line

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/invite-codes", {
        code: customCode.trim().toUpperCase() || null,
        label: matchLabel,
        special_pool_id: sp.special_pool_id,
      });
      setCustomCode("");
      toast.success("Código creado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copiado`);
  };

  const remove = async (code) => {
    if (!confirm(`¿Eliminar el código ${code}?`)) return;
    try {
      await api.delete(`/admin/invite-codes/${code}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const unusedCount = codes.filter((c) => !c.used_by_user_id).length;
  const usedCount = codes.length - unusedCount;

  return (
    <div className="bg-black/40 border border-zinc-800 p-3 space-y-3" data-testid={`sp-invites-${sp.special_pool_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--c-yellow)] font-mono-data">Códigos de invitación</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">Cada código permite a 1 usuario unirse a esta polla especial</div>
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-mono-data">
          <span>Total: <span className="text-white font-bold">{codes.length}</span></span>
          <span>Disp.: <span className="text-[var(--c-lime)] font-bold">{unusedCount}</span></span>
          <span>Usados: <span className="text-[var(--c-peach)] font-bold">{usedCount}</span></span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Etiqueta</label>
          <input value={matchLabel} readOnly
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm rounded-sm text-zinc-300" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Código manual (opcional)</label>
          <input value={customCode} onChange={(e) => setCustomCode(e.target.value)} maxLength={20}
            data-testid={`sp-invite-custom-${sp.special_pool_id}`}
            placeholder="Dejar vacío = auto-generado"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-[var(--c-yellow)] rounded-sm uppercase" />
        </div>
        <button onClick={create} disabled={busy} data-testid={`sp-invite-create-${sp.special_pool_id}`}
          className="bg-[var(--c-yellow)] text-black font-bold uppercase tracking-wider px-3 py-2 text-xs disabled:opacity-50">
          + Crear código
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs">Cargando…</div>
      ) : codes.length === 0 ? (
        <div className="text-zinc-500 text-xs italic">Aún no hay códigos para esta polla.</div>
      ) : (
        <ul className="divide-y divide-zinc-800 bg-zinc-900 border border-zinc-800">
          {codes.map((c) => (
            <li key={c.code} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs" data-testid={`sp-invite-row-${c.code}`}>
              <div className="col-span-3 font-mono-data font-bold text-[var(--c-yellow)] tracking-wider truncate">{c.code}</div>
              <div className="col-span-3 text-zinc-300 truncate">{c.label || matchLabel}</div>
              <div className="col-span-3">
                {c.used_by_user_id ? (
                  <span className="text-[10px] uppercase tracking-widest text-[var(--c-peach)] font-mono-data">USADO</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-[var(--c-lime)] font-mono-data">DISPONIBLE</span>
                )}
              </div>
              <div className="col-span-2 text-zinc-400 truncate">{c.used_by_email || ""}</div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button onClick={() => copy(c.code)} title="Copiar" className="p-1.5 border border-zinc-800 hover:border-white">
                  <Copy size={11} />
                </button>
                {!c.used_by_user_id && (
                  <button onClick={() => remove(c.code)} title="Eliminar" className="p-1.5 border border-zinc-800 hover:border-[var(--c-red)] text-[var(--c-red)]">
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] rounded-sm text-sm" />
    </div>
  );
}

function PctInput({ label, value, setValue, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">{label}</label>
      <input type="number" min={0} max={100} value={value} onChange={(e) => setValue(e.target.value)} data-testid={testid}
        className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-zinc-300 rounded-sm" />
    </div>
  );
}

function TeamEditor({ side, name, setName, img, onUpload, sp }) {
  return (
    <div className="bg-black/40 border border-zinc-800 p-3 space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
        {side === "home" ? "Equipo Local" : "Equipo Visitante"}
      </div>
      <div className="flex items-center gap-3">
        {img ? (
          <img src={img} alt={name} className="w-12 h-12 object-contain border border-zinc-800 bg-black p-1" />
        ) : (
          <div className="w-12 h-12 border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600">⚽</div>
        )}
        <label className="cursor-pointer text-xs uppercase tracking-wider text-zinc-300 border border-zinc-700 hover:border-[var(--c-yellow)] px-2 py-1">
          {img ? "Reemplazar" : "Subir imagen"}
          <input type="file" accept="image/*" className="hidden" data-testid={`sp-${side}-img-${sp.special_pool_id}`}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f); }} />
        </label>
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)}
        placeholder={side === "home" ? "Ej: Real Madrid" : "Ej: PSG"}
        data-testid={`sp-${side}-name-${sp.special_pool_id}`}
        className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-yellow)] rounded-sm text-sm" />
    </div>
  );
}



function PoolsPanel() {
  const [pools, setPools] = useState([]);
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [expandedPool, setExpandedPool] = useState(null);
  const [members, setMembers] = useState({});

  const load = async () => {
    const [p, u] = await Promise.all([
      api.get("/admin/pools"),
      api.get("/admin/users"),
    ]);
    setPools(p.data);
    setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const loadMembers = async (pid) => {
    const r = await api.get(`/admin/pools/${pid}/members`);
    setMembers((m) => ({ ...m, [pid]: r.data }));
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (name.trim().length < 3) {
      toast.error("Nombre mínimo 3 caracteres");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/pools", { name: name.trim(), description: description.trim() || null });
      setName(""); setDescription("");
      toast.success("Polla creada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const remove = async (pool) => {
    if (pool.is_default) { toast.error("No se puede eliminar la polla general"); return; }
    if (!confirm(`¿Eliminar la polla "${pool.name}" y todos sus miembros?`)) return;
    try {
      await api.delete(`/admin/pools/${pool.pool_id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggleExpand = async (pid) => {
    if (expandedPool === pid) { setExpandedPool(null); return; }
    setExpandedPool(pid);
    if (!members[pid]) await loadMembers(pid);
  };

  const addMember = async (pid, uid) => {
    try {
      await api.post(`/admin/pools/${pid}/members`, { user_id: uid });
      toast.success("Agregado");
      await loadMembers(pid);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const removeMember = async (pid, uid) => {
    try {
      await api.delete(`/admin/pools/${pid}/members/${uid}`);
      toast.success("Removido");
      await loadMembers(pid);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-4" data-testid="pools-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="flex items-center gap-3 mb-3">
          <Layers className="text-[var(--c-mint)]" size={20} />
          <div className="font-display text-lg uppercase">Crear nueva polla / espacio</div>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Crea espacios independientes para que diferentes grupos compitan entre sí: familia, amigos del colegio, compañeros de trabajo, etc. Cada polla tiene su propio ranking.
        </p>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Nombre *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="pool-name-input"
              placeholder="Ej. Familia, Trabajo, Colegio"
              className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-mint)] rounded-sm" required minLength={3} maxLength={60} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Descripción</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="pool-description-input"
              placeholder="(opcional)"
              className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-mint)] rounded-sm" maxLength={200} />
          </div>
          <button type="submit" disabled={busy} data-testid="pool-create-button"
            className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs flex items-center justify-center gap-2 h-10 disabled:opacity-50">
            <Plus size={14} /> Crear polla
          </button>
        </form>
      </div>

      <div className="space-y-3">
        {pools.map((p) => {
          const expanded = expandedPool === p.pool_id;
          const poolMembers = members[p.pool_id] || [];
          const memberIds = new Set(poolMembers.map((m) => m.user_id));
          const eligibleToAdd = users.filter((u) => !memberIds.has(u.user_id));
          return (
            <div key={p.pool_id} className="bg-zinc-900 border border-zinc-800" data-testid={`pool-card-${p.pool_id}`}>
              <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg uppercase">{p.name}</div>
                    {p.is_default && <span className="text-[9px] uppercase tracking-widest font-mono-data text-[var(--c-yellow)]">★ DEFAULT</span>}
                  </div>
                  {p.description && <div className="text-xs text-zinc-400 mt-1">{p.description}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="font-mono-data text-lg font-bold text-[var(--c-mint)]">{p.member_count}</div>
                    <div className="text-[9px] uppercase tracking-widest font-mono-data text-zinc-500">MIEMBROS</div>
                  </div>
                  <button onClick={() => toggleExpand(p.pool_id)} data-testid={`pool-expand-${p.pool_id}`}
                    className="px-3 py-2 text-[10px] uppercase tracking-widest border border-zinc-800 hover:border-white">
                    {expanded ? "Cerrar" : "Miembros"}
                  </button>
                  {!p.is_default && (
                    <button onClick={() => remove(p)} data-testid={`pool-delete-${p.pool_id}`}
                      className="p-2 border border-zinc-800 hover:border-[var(--c-red)] text-[var(--c-red)]">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div className="border-t border-zinc-800 px-4 py-3">
                <PoolPrizeEditor pool={p} onSaved={load} />
              </div>
              {expanded && (
                <div className="border-t border-zinc-800 px-4 py-3 space-y-3" data-testid={`pool-members-${p.pool_id}`}>
                  {/* Add member */}
                  {!p.is_default && eligibleToAdd.length > 0 && (
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Agregar usuario</label>
                      <select onChange={(e) => { if (e.target.value) { addMember(p.pool_id, e.target.value); e.target.value = ""; } }}
                        data-testid={`pool-add-member-${p.pool_id}`}
                        className="bg-black border border-zinc-800 px-3 py-2 text-xs outline-none focus:border-[var(--c-lime)] w-full md:w-72">
                        <option value="">— Seleccionar usuario —</option>
                        {eligibleToAdd.map((u) => <option key={u.user_id} value={u.user_id}>{u.nickname} ({u.email})</option>)}
                      </select>
                    </div>
                  )}
                  {/* Members list */}
                  {poolMembers.length === 0 ? (
                    <div className="text-zinc-500 text-sm">Sin miembros aún.</div>
                  ) : (
                    <div className="space-y-1">
                      {poolMembers.map((m) => (
                        <div key={m.user_id} className="flex items-center justify-between gap-2 px-2 py-1.5 border border-zinc-800 text-sm">
                          <div className="min-w-0">
                            <div className="font-semibold">{m.nickname}</div>
                            <div className="text-[10px] text-zinc-500">{m.email}</div>
                          </div>
                          {!p.is_default && (
                            <button onClick={() => removeMember(p.pool_id, m.user_id)} data-testid={`pool-remove-member-${p.pool_id}-${m.user_id}`}
                              className="p-1.5 border border-zinc-800 hover:border-[var(--c-red)] text-[var(--c-red)]" title="Remover">
                              <X size={10} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <UserVisiblePools users={users} pools={pools} />
    </div>
  );
}

function UserVisiblePools({ users, pools }) {
  const [selectedUser, setSelectedUser] = useState("");
  const [visibleIds, setVisibleIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadFor = async (uid) => {
    if (!uid) { setVisibleIds([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/admin/user/${uid}/visible-pools`);
      setVisibleIds(r.data.visible_pool_ids || []);
    } catch {
      toast.error("No se pudo cargar la configuración del usuario");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFor(selectedUser); }, [selectedUser]);

  const toggle = (pid) => {
    setVisibleIds((cur) => cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]);
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.put(`/admin/user/${selectedUser}/visible-pools`, { pool_ids: visibleIds });
      toast.success("Pollas visibles actualizadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 p-5" data-testid="visible-pools-panel">
      <div className="flex items-center gap-3 mb-3">
        <Layers className="text-[var(--c-sky)]" size={20} />
        <div className="font-display text-lg uppercase">Pollas visibles por usuario</div>
      </div>
      <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
        Controla qué pollas puede ver cada usuario en su sección "Tabla de Posiciones". Por defecto, cada usuario solo ve la polla del código de invitación con el que se registró.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-4">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Usuario</label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            data-testid="visible-pools-user-select"
            className="w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-sky)] rounded-sm text-sm"
          >
            <option value="">— Selecciona un usuario —</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.nickname} ({u.email})</option>
            ))}
          </select>
        </div>
        {selectedUser && (
          <button
            onClick={save}
            disabled={saving}
            data-testid="visible-pools-save"
            className="bg-[var(--c-sky)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs h-10 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        )}
      </div>
      {selectedUser && !loading && (
        <div className="space-y-2">
          {pools.map((p) => {
            const checked = visibleIds.includes(p.pool_id);
            return (
              <label
                key={p.pool_id}
                data-testid={`visible-pool-toggle-${p.pool_id}`}
                className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors ${checked ? "border-[var(--c-sky)] bg-[rgba(56,189,248,0.06)]" : "border-zinc-800 hover:border-zinc-700"}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p.pool_id)}
                  className="accent-[var(--c-sky)]"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {p.name}
                    {p.is_default && <span className="text-[9px] uppercase tracking-widest font-mono-data text-[var(--c-yellow)]">★ DEFAULT</span>}
                  </div>
                  {p.description && <div className="text-[10px] text-zinc-500">{p.description}</div>}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function resolveBgUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = API_BASE.replace(/\/api$/, "");
  return `${base}${url}`;
}

function BackgroundsPanel() {
  const [data, setData] = useState({ sections: [], gallery: [] });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [heroMode, setHeroMode] = useState("text");
  const [savingMode, setSavingMode] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, pc] = await Promise.all([
        api.get("/admin/section-backgrounds"),
        api.get("/page-content"),
      ]);
      setData(r.data);
      setHeroMode(pc.data?.login_hero_mode || "text");
    } catch (e) {
      toast.error("No se pudo cargar la configuración de fondos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateHeroMode = async (mode) => {
    setSavingMode(true);
    try {
      await api.put("/admin/page-content", { login_hero_mode: mode });
      setHeroMode(mode);
      invalidateBackgroundsCache();
      toast.success("Modo del hero del login actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingMode(false);
    }
  };

  const setBg = async (key, image_url) => {
    setSavingKey(key);
    try {
      await api.put(`/admin/section-backgrounds/${key}`, { image_url });
      invalidateBackgroundsCache();
      toast.success(image_url ? "Fondo aplicado" : "Fondo eliminado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingKey(null);
    }
  };

  const uploadAndSet = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecciona una imagen"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Máximo 8 MB"); return; }
    setSavingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/section-backgrounds/${key}`, { image_url: up.data?.url });
      invalidateBackgroundsCache();
      toast.success("Imagen subida y aplicada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir imagen");
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="backgrounds-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="flex items-center gap-3 mb-2">
          <ImageIcon size={18} className="text-[var(--c-lime)]" />
          <h2 className="font-display text-xl uppercase">Fondos de secciones</h2>
        </div>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Asigna una imagen de fondo a cada sección del menú. Si no eliges ninguna, esa sección mantiene el fondo oscuro por defecto.
          Las imágenes se muestran a pantalla completa con una capa oscura encima para que el contenido siga legible.
        </p>
      </div>

      {data.gallery?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-3">Galería de imágenes</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {data.gallery.map((g) => (
              <div key={g.id} className="border border-zinc-800 overflow-hidden" data-testid={`gallery-${g.id}`}>
                <img src={resolveBgUrl(g.url)} alt={g.label} className="w-full h-32 object-cover" />
                <div className="p-2 text-[11px] text-zinc-400 truncate" title={g.label}>{g.label}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
            Para usar una imagen de la galería en una sección, copia su URL desde el navegador (clic derecho → "Copiar dirección de la imagen") y pégala en el campo de URL de la sección de abajo.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {data.sections.map((s) => {
          const preview = resolveBgUrl(s.image_url);
          const busy = savingKey === s.key;
          return (
            <div key={s.key} className="bg-zinc-900 border border-zinc-800 p-4 flex flex-col gap-4" data-testid={`bg-row-${s.key}`}>
              {s.key === "login_hero" && (
                <div className="bg-black border border-zinc-800 p-3" data-testid="login-hero-mode-control">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">¿Qué mostrar en el bloque hero del login?</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "text", label: "Texto de colores", color: "var(--c-lime)" },
                      { value: "image", label: "Imagen subida", color: "var(--c-sky)" },
                      { value: "none", label: "Nada", color: "var(--c-red)" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => updateHeroMode(opt.value)}
                        disabled={savingMode}
                        data-testid={`hero-mode-${opt.value}`}
                        className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${
                          heroMode === opt.value
                            ? "font-bold"
                            : "border-zinc-800 text-zinc-400 hover:text-white"
                        }`}
                        style={heroMode === opt.value ? { backgroundColor: opt.color, color: "black", borderColor: opt.color } : {}}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                    "Nada" oculta tanto el texto como la imagen — solo se muestran los módulos de registro y de iniciar sesión.
                  </div>
                </div>
              )}
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="w-full md:w-32 h-20 bg-black border border-zinc-800 overflow-hidden flex-shrink-0">
                  {preview ? (
                    <img src={preview} alt={s.label} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-widest text-zinc-600 font-mono-data">Fondo oscuro</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase leading-tight" data-testid={`bg-label-${s.key}`}>{s.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{s.key}</div>
                  <BgUrlInput initial={s.image_url || ""} onSave={(url) => setBg(s.key, url || null)} testid={`bg-url-${s.key}`} />
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <label className={`cursor-pointer inline-flex items-center gap-2 border border-dashed border-zinc-700 hover:border-[var(--c-lime)] hover:text-[var(--c-lime)] px-3 py-2 text-xs uppercase tracking-wider text-zinc-300 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload size={14} /> Subir
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      data-testid={`bg-upload-${s.key}`}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadAndSet(s.key, f); }}
                    />
                  </label>
                  {preview && (
                    <button
                      onClick={() => setBg(s.key, null)}
                      disabled={busy}
                      data-testid={`bg-clear-${s.key}`}
                      className="inline-flex items-center gap-2 border border-[var(--c-red)] text-[var(--c-red)] px-3 py-2 text-xs uppercase tracking-wider hover:bg-[var(--c-red)] hover:text-black disabled:opacity-50"
                    >
                      <RotateCcw size={14} /> Quitar
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BgUrlInput({ initial, onSave, testid }) {
  const [val, setVal] = useState(initial);
  useEffect(() => { setVal(initial); }, [initial]);
  const dirty = (val || "").trim() !== (initial || "").trim();
  return (
    <div className="flex gap-2 items-stretch mt-2">
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="…o pega una URL pública de imagen"
        data-testid={testid}
        className="flex-1 bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-lime)] rounded-sm text-xs"
      />
      {dirty && (
        <button
          onClick={() => onSave(val.trim())}
          data-testid={`${testid}-save`}
          className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-3 py-2 text-xs flex items-center gap-1"
        >
          <Save size={12} /> Guardar
        </button>
      )}
    </div>
  );
}



function TeamsPanel({ teams, matches, onChange }) {
  const groups = useMemo(() => {
    const map = {};
    for (const t of teams) {
      const g = t.group || "Sin grupo";
      if (!map[g]) map[g] = [];
      map[g].push(t);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [teams]);

  const allGroups = Object.keys(groups).sort();
  const matchesByTeam = useMemo(() => {
    const m = {};
    for (const x of matches) {
      if (x.home) m[x.home] = (m[x.home] || 0) + 1;
      if (x.away) m[x.away] = (m[x.away] || 0) + 1;
    }
    return m;
  }, [matches]);

  return (
    <div className="space-y-4" data-testid="teams-panel">
      <div className="bg-zinc-900 border border-zinc-800 p-5">
        <div className="font-display text-lg uppercase mb-1">Equipos y grupos</div>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Reemplaza, renombra o mueve cualquier equipo entre grupos en caso de cambios en los participantes oficiales. Todos los partidos asociados se actualizan automáticamente.
        </p>
      </div>

      <NewTeamForm allGroups={allGroups} onChange={onChange} />

      {allGroups.map((g) => (
        <div key={g} className="bg-zinc-900 border border-zinc-800">
          <div className="px-4 py-2 border-b border-zinc-800 font-display text-sm uppercase">Grupo {g}</div>
          {groups[g].map((t) => (
            <TeamEditRow key={t.code} team={t} allGroups={allGroups} matchCount={matchesByTeam[t.code] || 0} onChange={onChange} />
          ))}
        </div>
      ))}
    </div>
  );
}

function NewTeamForm({ allGroups, onChange }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [flag, setFlag] = useState("");
  const [group, setGroup] = useState(allGroups[0] || "A");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !name.trim() || !flag.trim()) {
      toast.error("Completa código, nombre y bandera (ISO)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/team", {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        flag: flag.trim().toLowerCase(),
        group,
      });
      toast.success("Equipo creado");
      setCode(""); setName(""); setFlag("");
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 p-4" data-testid="new-team-section">
      <button
        onClick={() => setOpen(!open)}
        data-testid="toggle-new-team"
        className="text-xs uppercase tracking-widest text-[var(--c-lime)] hover:underline flex items-center gap-2"
      >
        <Plus size={14} /> {open ? "Cancelar" : "Agregar nuevo equipo"}
      </button>
      {open && (
        <form onSubmit={submit} className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div className="col-span-1">
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Código *</label>
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={3}
              data-testid="new-team-code" placeholder="USA"
              className="w-full bg-black border border-zinc-800 px-2 py-2 outline-none focus:border-[var(--c-lime)] text-xs uppercase" />
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Nombre *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-team-name"
              placeholder="Estados Unidos"
              className="w-full bg-black border border-zinc-800 px-2 py-2 outline-none focus:border-[var(--c-lime)] text-xs" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Bandera (ISO)</label>
            <input value={flag} onChange={(e) => setFlag(e.target.value.toLowerCase())} maxLength={3}
              data-testid="new-team-flag" placeholder="us"
              className="w-full bg-black border border-zinc-800 px-2 py-2 outline-none focus:border-[var(--c-lime)] text-xs lowercase" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">Grupo</label>
            <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="new-team-group"
              className="w-full bg-black border border-zinc-800 px-2 py-2 outline-none focus:border-[var(--c-lime)] text-xs">
              {allGroups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="col-span-2 md:col-span-5">
            <button type="submit" disabled={busy} data-testid="new-team-submit"
              className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs disabled:opacity-50">
              Crear equipo
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function TeamEditRow({ team, allGroups, matchCount, onChange }) {
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(team.code);
  const [name, setName] = useState(team.name);
  const [flag, setFlag] = useState(team.flag);
  const [group, setGroup] = useState(team.group);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setCode(team.code); setName(team.name); setFlag(team.flag); setGroup(team.group);
  }, [team]);

  const save = async () => {
    try {
      const payload = {};
      if (code.trim() && code !== team.code) payload.code = code.trim().toUpperCase();
      if (name.trim() && name !== team.name) payload.name = name.trim();
      if (flag.trim() && flag !== team.flag) payload.flag = flag.trim().toLowerCase();
      if (group && group !== team.group) payload.group = group;
      if (Object.keys(payload).length === 0) {
        setEditing(false);
        return;
      }
      await api.put(`/admin/team/${team.code}`, payload);
      toast.success(`${team.code} actualizado`);
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const uploadFlag = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecciona una imagen"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Máximo 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/team/${team.code}`, { flag_image_url: up.data?.url });
      toast.success("Bandera personalizada subida");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir bandera");
    } finally {
      setUploading(false);
    }
  };

  const clearCustomFlag = async () => {
    try {
      await api.put(`/admin/team/${team.code}`, { flag_image_url: null });
      toast.success("Bandera personalizada quitada");
      onChange();
    } catch {
      toast.error("Error al quitar");
    }
  };

  const flagSrc = team.flag_image_url
    ? (team.flag_image_url.startsWith("http") ? team.flag_image_url : `${API_BASE.replace(/\/api$/, "")}${team.flag_image_url}`)
    : `https://flagcdn.com/w80/${team.flag}.png`;

  return (
    <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3 flex-wrap" data-testid={`team-row-${team.code}`}>
      <img src={flagSrc} alt={team.code} className="w-9 h-6 object-cover flex-shrink-0" />
      {editing ? (
        <>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={3}
            data-testid={`team-edit-code-${team.code}`}
            className="bg-black border border-zinc-800 px-2 py-1 text-xs uppercase w-16" />
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`team-edit-name-${team.code}`}
            className="bg-black border border-zinc-800 px-2 py-1 text-xs flex-1 min-w-[150px]" />
          <input value={flag} onChange={(e) => setFlag(e.target.value.toLowerCase())} maxLength={3}
            data-testid={`team-edit-flag-${team.code}`}
            className="bg-black border border-zinc-800 px-2 py-1 text-xs lowercase w-14" />
          <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid={`team-edit-group-${team.code}`}
            className="bg-black border border-zinc-800 px-2 py-1 text-xs">
            {allGroups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <div className="flex gap-1">
            <button onClick={save} data-testid={`team-save-${team.code}`} className="p-2 bg-[var(--c-lime)] text-black" title="Guardar"><Save size={12} /></button>
            <button onClick={() => setEditing(false)} className="p-2 border border-zinc-800"><X size={12} /></button>
          </div>
        </>
      ) : (
        <>
          <div className="font-mono-data text-xs text-zinc-500 w-10">{team.code}</div>
          <div className="text-sm flex-1 min-w-[120px]">{team.name}</div>
          <div className="font-mono-data text-[10px] text-zinc-500 w-12">grp {team.group}</div>
          <div className="font-mono-data text-[10px] text-zinc-500 w-20">{matchCount} partidos</div>
          <label className={`cursor-pointer inline-flex items-center gap-1 border border-dashed border-zinc-700 hover:border-[var(--c-lime)] hover:text-[var(--c-lime)] px-2 py-1.5 text-[10px] uppercase tracking-widest text-zinc-300 ${uploading ? "opacity-50 pointer-events-none" : ""}`} title="Subir bandera personalizada">
            <Upload size={11} /> Bandera
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
              className="hidden"
              data-testid={`team-flag-upload-${team.code}`}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadFlag(f); }}
            />
          </label>
          {team.flag_image_url && (
            <button onClick={clearCustomFlag} data-testid={`team-flag-clear-${team.code}`} className="p-1.5 border border-[var(--c-red)] text-[var(--c-red)] hover:bg-[var(--c-red)] hover:text-black" title="Quitar bandera personalizada">
              <RotateCcw size={11} />
            </button>
          )}
          <button onClick={() => setEditing(true)} data-testid={`team-edit-${team.code}`} className="p-2 border border-zinc-800 hover:border-white" title="Editar"><Edit3 size={12} /></button>
        </>
      )}
    </div>
  );
}

